// 11 §3.1 Boot: atlas load, dye-ramp node spike placeholder (§4.4), then follows RouteStore:
// the `Encounter` scene runs while the route is 'encounter' (or its Spoils, drawn over the finished
// board, docs/84) and stops otherwise. World, Lobby
// and Aftermath have no Phaser backdrop yet (plate B minus the map art), so the canvas stays navy.
import Phaser from 'phaser';
import type { GameBoot } from '../Game';
import { useRoute } from '../../core/stores/route';
import { loadEmotes } from '../board/EmoteLayer';
import { HUGE_ART } from '../hugeArt';
import { CREATURE_ART } from '../creatureArt';
import { PUZZLE_ATLAS } from '../board/puzzleArt';
import { OBJECT_ATLAS } from '../matter/objectArt';

export class Boot extends Phaser.Scene {
  constructor() { super('Boot'); }

  preload(): void {
    const boot = this.registry.get('boot') as GameBoot;
    for (const [key, base] of Object.entries(boot.manifest.atlases)) {
      this.load.atlas(key, `${base}.png`, `${base}.json`);
    }
    loadEmotes(this.load);   // docs/62
    // docs/92 HM5: the commissioned huge bodies
    for (const a of Object.values(HUGE_ART)) this.load.image(a.key, a.url);
    // docs/117: the commissioned one-square creatures (the Thing Below's arms)
    for (const a of Object.values(CREATURE_ART)) this.load.image(a.key, a.url);
    // the commissioned puzzle pieces (placeholder art, tools/puzzle-art.py)
    this.load.atlas(PUZZLE_ATLAS.key, PUZZLE_ATLAS.png, PUZZLE_ATLAS.json);
    // docs/189: the everyday objects (tools/object-art.py)
    this.load.atlas(OBJECT_ATLAS.key, OBJECT_ATLAS.png, OBJECT_ATLAS.json);
    this.load.on(Phaser.Loader.Events.FILE_LOAD_ERROR, (file: { key: string }) => {
      console.warn('[boot] failed to load', file.key);
    });
  }

  create(): void {
    const boot = this.registry.get('boot') as GameBoot;
    // §4.4 dye-ramp self-test would run here; CPU fallback until the render node exists.
    this.registry.set('dyeNode', 'cpu-fallback');
    this.registry.set('cell', boot.manifest.cell);
    this.registry.set('bootReady', true);

    if (boot.gallery) { this.scene.launch('Gallery'); return; }

    // stop and launch are queued ops, so `isActive` still reads true right after a stop: a route that
    // leaves the encounter and comes straight back within a frame would otherwise skip the launch and
    // leave the board blank. Track what was last asked for instead (the queue runs the ops in order).
    let running = false;
    const apply = (screen: string) => {
      const want = screen === 'encounter' || screen === 'spoils';
      if (want === running) return;
      running = want;
      if (want) this.scene.launch('Encounter'); else this.scene.stop('Encounter');
    };
    apply(useRoute.getState().screen);
    useRoute.subscribe((r, p) => { if (r.screen !== p.screen) apply(r.screen); });
  }
}
